import React, { useState } from 'react'
import { object } from 'prop-types'
import { branch } from 'baobab-react/higher-order'

import allValExists from 'utils/allValExists'

import BaseTemplate from 'components/templates/BaseTemplate'
import VideoModal from 'components/molecules/VideoModal'
import DetailRelatedCampus from 'components/organisms/DetailRelatedCampus'
import DetailRelatedScholarships from 'components/organisms/DetailRelatedScholarships'

import ScholarshipPageBanner from './ScholarshipPageBanner'
import ScholarshipPageBannerContent from './ScholarshipPageBannerContent'
import ScholarshipStickyBottom from './ScholarshipStickyBottomContent'
import ScholarshipDescription from './ScholarshipDescription'
import ScholarshipFaqs from './ScholarshipFaqs'
import ScholarshipBenefit from './ScholarshipBenefit'
import ScholarshipRegistration from './ScholarshipRegistration'
import ScholarshipTermsCondition from './ScholarshipTermsCondition'
import ScholarshipTestimonies from './ScholarshipTestimonies'
import ScholarshipContacts from './ScholarshipContacts'
import ScholarshipSponsors from './ScholarshipSponsors'

const ScholarshipDetail = ({
  scholarship: {
    id,
    name,
    logo,
    liked,
    short_info: shortInfo,
    providers,
    purposes,
    paid,
    banner,
    banner_video_url: videoUrl,
    brochure,
    description,
    description_section_title: descSectionTitle,
    faqs,
    benefit,
    terms_condition: termsCondition,
    contacts,
    testimonies_title: testimoniesTitle,
    related_campus_title: relatedCampusTitle,
    related_scholarship_title: relatedScholarshipsTitle,
    related_sponsorship_title: relatedSponsorshipTitle,
    registration,
  },
  testimonies,
  relatedCampus,
  relatedScholarships,
  sponsorships,
}) => {
  const [showVideo, setShowVideo] = useState(false)
  const sections = [
    {
      target: '#description',
      text: 'Deskripsi',
      show: !!(descSectionTitle || description),
    },
    {
      target: '#faqs',
      text: 'Info Penting',
      show: !!(faqs.title || faqs.items.length > 0),
    },
    {
      target: '#benefits',
      text: 'Benefit',
      show: allValExists(benefit, ['title']),
    },
    {
      target: '#registration',
      text: 'Pendaftaran',
      show: !!(registration.description || registration.steps.length > 0),
    },
    {
      target: '#terms-condition',
      text: 'Persyaratan',
      show: allValExists(termsCondition, ['title']),
    },
    {
      target: '#testimonies',
      text: 'Testimoni',
      show: !!(testimoniesTitle || testimonies.length > 0),
    },
    {
      target: '#contacts',
      text: 'Kontak',
      show: !!contacts.title,
    },
    {
      target: '#related-campus',
      text: 'Kampus',
      show: relatedCampus.length > 0,
    },
    {
      target: '#related-scholarships',
      text: 'Beasiswa',
      show: relatedScholarships.length > 0,
    },
    {
      target: '#sponsorships',
      text: 'Sponsor',
      show: !!(relatedSponsorshipTitle || sponsorships.length > 0),
    },
  ]
  const testimoniesData = testimonies && testimonies.map(testimony => ({
    name: testimony.student_name,
    img: testimony.student_photo_url,
    imgAlt: testimony.student_name,
    majorName: testimony.study_field_name,
    schoolName: testimony.school_name,
    testimony: testimony.description,
  }))
  const socmeds = {
    facebook: `https://fb.com/${contacts.fbUsername}`,
    instagram: `https://instagram.com/${contacts.instagram}`,
    twitter: `https://twitter.com/${contacts.twitter}`,
  }
  const [hasTriggered, setHasTriggered] = useState(false)

  return (
    <BaseTemplate
      sections={sections}
      firstSection=".scholarship-page-banner"
      backToTopPostAdjusment={603}
      stickyBottomContent={
        <ScholarshipStickyBottom
          logo={logo}
          name={name}
          brochure={brochure}
          registrationUrl={registration.url}
          likeStatus={liked}
          likeTarget={id}
          paid={paid}
          hasTriggered={hasTriggered}
          setHasTriggered={setHasTriggered}
        />
      }
      actionContent={{
        likeStatus: liked,
        likeTarget: id,
        kind: 'scholarships',
        shareTitle: name,
        shareText: `Hai, yuk cek ${name} di @quippercampus. Ada beasiswa kuliah buat kamu hingga 100% lho. Daftar sekarang sebelum pendaftarannya ditutup! #beasiswa #scholarship`,
      }}
      useNewHeader
      useStickyBottom
      useBackToTop
      useCTABottom={Boolean(registration.url)}
    >
      <div className="scholarship-detail">
        <ScholarshipPageBanner
          title={name}
          likeStatus={liked}
          likeTarget={id}
          kind="scholarships"
          logo={logo}
          imgSrcDesktop={banner.desktop}
          imgSrcMobile={banner.mobile}
          mainVideo={{
            source: videoUrl,
          }}
          onVideoClick={() => setShowVideo(true)}
          content={
            <ScholarshipPageBannerContent
              registrationUrl={registration.url}
              brochure={brochure}
              socialMedia={socmeds}
              shortInfo={shortInfo}
              providers={providers}
              purposes={purposes}
              paid={paid}
              hasTriggered={hasTriggered}
              setHasTriggered={setHasTriggered}
            />
          }
          inverted
        />
        {
          !!(descSectionTitle || description) &&
          <ScholarshipDescription
            title={descSectionTitle}
            description={description}
          />
        }
        {
          !!(faqs.title || faqs.items.length > 0) &&
          <ScholarshipFaqs faqData={faqs} />
        }
        {
          allValExists(benefit, ['title']) &&
          <ScholarshipBenefit
            title={benefit.title}
            tuitionFee={benefit.tuition_fee}
            livingCost={benefit.living_cost}
            description={benefit.description}
          />
        }
        {
          !!(registration.description || registration.steps.length > 0) &&
          <ScholarshipRegistration
            title={registration.title}
            description={registration.description}
            steps={registration.steps}
            paid={paid}
            hasTriggered={hasTriggered}
            setHasTriggered={setHasTriggered}
          />
        }
        {
          allValExists(termsCondition, ['title']) &&
          <ScholarshipTermsCondition
            title={termsCondition.title}
            description={termsCondition.description}
          />
        }
        {
          !!(testimoniesTitle || testimonies.length > 0) &&
          <ScholarshipTestimonies
            title={testimoniesTitle}
            testimonies={testimoniesData}
          />
        }
        {
          !!contacts.title &&
          <ScholarshipContacts contacts={contacts}/>
        }
        {
          (relatedCampus.length > 0 || relatedScholarships.length > 0) &&
          <div className="scholarship-detail__related-thumbnails">
            {
              relatedCampus.length > 0 && <DetailRelatedCampus
                sectionTitle={relatedCampusTitle || 'Kampus Terkait'}
                schools={relatedCampus}
                useAccentBg={false}
                paddingTop={0}
                paddingBottom={80}
              />
            }
            {
              relatedScholarships.length > 0 && <DetailRelatedScholarships
                sectionTitle={relatedScholarshipsTitle || 'Beasiswa Terkait'}
                scholarships={relatedScholarships}
                useAccentBg={false}
                paddingTop={0}
                paddingBottom={80}
              />
            }
          </div>
        }
        {
          !!(relatedSponsorshipTitle || sponsorships.length > 0) &&
          <ScholarshipSponsors
            sectionTitle={relatedSponsorshipTitle}
            sponsorships={sponsorships}
          />
        }
        <VideoModal
          isOpen={showVideo}
          onVideoClose={() => setShowVideo(false)}
          videoSource={videoUrl}
        />
      </div>
      <style jsx>
        {`
          .scholarship-detail {
            &__related-thumbnails {
              padding-top: 80px;
            }
            :global(.detail-related-campus h2),
            :global(.detail-related-scholarships h2) {
              text-align: center;
            }
            :global(.detail-related-campus .ThumbnailSlider) {
              margin-bottom: 0px;
            }
          }
        `}
      </style>
    </BaseTemplate>
  )
}

ScholarshipDetail.propTypes = {
  scholarship: object,
}

export { ScholarshipDetail }

export default branch({
  scholarship: ['serverProps', 'scholarship'],
  testimonies: ['serverProps', 'testimonies'],
  relatedCampus: ['serverProps', 'relatedCampus'],
  relatedScholarships: ['serverProps', 'relatedScholarships'],
  sponsorships: ['serverProps', 'sponsorships'],
}, ScholarshipDetail)
